import { useMemo } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { generateDaysArray } from "../shared/calendarUtils";
import { SlotsByService, BookingsByService } from "../types";
import { DAYS_WINDOW, UNIFIED_15MIN_SLOTS, PSY_HOURLY_SLOTS, SATURDAY_WEEKDAY, SATURDAY_HIGHLIGHT_COLOR } from "../constants";
import { ReservedSlotCell } from "../ReservedSlotCell";
import { NfbBlock } from "./NfbBlock";
import {
    PSY_DURATION_ROWS,
    NFB_DURATION_ROWS,
    GRID_STEP_MINUTES,
    LIGHT_GREY,
    PSY_RESERVATION_BLUE,
    PSY_BOOKING_ONLY_BG,
    NFB_BLOCKED_GREY,
    getTimeMinusMinutes,
    isPsyStartTime,
    isNfbStartTime,
    getBackgroundColor,
} from "./gridUtils";

type ReservedSlotsGridProps = {
    startDate: Date;
    getSlotsForDateAndTime: (dateKey: string, time: string) => SlotsByService;
    getBookingsForDateAndTime: (dateKey: string, time: string) => BookingsByService;
    getAvailabilityStatus: (dateKey: string, time: string, service?: "psy" | "nfb") => "available" | "time_off" | null;
    getNfbCapacity: (dateKey: string, time: string) => number;
    isNfbBlocked: (dateKey: string, time: string) => boolean;
    showPsy: boolean;
    showNfb: boolean;
    showOnlyMismatched: boolean;
};

export const ReservedSlotsGrid = ({
    startDate,
    getSlotsForDateAndTime,
    getBookingsForDateAndTime,
    getAvailabilityStatus,
    getNfbCapacity,
    isNfbBlocked,
    showPsy,
    showNfb,
    showOnlyMismatched,
}: ReservedSlotsGridProps) => {
    const allDays = useMemo(() => generateDaysArray(startDate, DAYS_WINDOW), [startDate]);

    const days = useMemo(() => {
        if (!showOnlyMismatched) return allDays;

        return allDays.filter((day) => {
            const matchesFiltersOnDay = PSY_HOURLY_SLOTS.some((time) => {
                const slots = getSlotsForDateAndTime(day.dateKey, time);

                const psyHasMismatch = slots.psy?.some((slot) => slot.hasScheduleMismatch === true);
                const psyMissedSessions = slots.psy?.some((slot) => slot.missedTwoConsecutiveSessions === true);
                const nfbHasMismatch = slots.nfb?.some((slot) => slot.hasScheduleMismatch === true);
                const nfbMissedSessions = slots.nfb?.some((slot) => slot.missedTwoConsecutiveSessions === true);
                const hasIssue = psyHasMismatch || nfbHasMismatch || psyMissedSessions || nfbMissedSessions;

                return hasIssue;
            });
            return matchesFiltersOnDay;
        });
    }, [allDays, showOnlyMismatched, getSlotsForDateAndTime]);

    const getPsyActiveSlot = (dateKey: string, time: string) => {
        for (let i = 0; i < PSY_DURATION_ROWS; i++) {
            const checkTime = getTimeMinusMinutes(time, i * GRID_STEP_MINUTES);
            if (isPsyStartTime(checkTime)) {
                const isPsyScheduled = getAvailabilityStatus(dateKey, checkTime, "psy") === "available";
                const reservations = getSlotsForDateAndTime(dateKey, checkTime).psy;
                const allBookings = getBookingsForDateAndTime(dateKey, checkTime).psy;
                // Reservations and PSY client sessions always show. Meetings / non-PSY "busy" blocks show
                // only within her psychology schedule — outside it they're noise for placing a PSY reservation.
                const bookings = isPsyScheduled ? allBookings : allBookings.filter((booking) => booking.isClientSession);
                if (reservations.length > 0 || bookings.length > 0) {
                    return { reservations, bookings, isStartRow: i === 0 };
                }
                break;
            }
        }
        return { reservations: [], bookings: [], isStartRow: false };
    };

    const getNfbActiveSlot = (dateKey: string, time: string, weekDay: number) => {
        for (let i = 0; i < NFB_DURATION_ROWS; i++) {
            const checkTime = getTimeMinusMinutes(time, i * GRID_STEP_MINUTES);
            if (isNfbStartTime(checkTime, weekDay)) {
                const reservations = getSlotsForDateAndTime(dateKey, checkTime).nfb;
                const bookings = getBookingsForDateAndTime(dateKey, checkTime).nfb;
                if (reservations.length > 0 || bookings.length > 0) {
                    const isEndRow = i === NFB_DURATION_ROWS - 1;
                    const capacity = getNfbCapacity(dateKey, checkTime);
                    return { reservations, bookings, isStartRow: i === 0, isEndRow, capacity };
                }
                break;
            }
        }
        return { reservations: [], bookings: [], isStartRow: false, isEndRow: false, capacity: 0 };
    };

    return (
        <TableContainer sx={{ maxHeight: "calc(100vh - 200px)", overflow: "auto", pb: 2 }}>
            <Table stickyHeader size="small" sx={{ borderCollapse: "collapse" }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                position: "sticky",
                                left: 0,
                                zIndex: 3,
                                backgroundColor: "background.paper",
                                fontWeight: "bold",
                                minWidth: 55,
                            }}
                        >
                            Hora
                        </TableCell>
                        {days.map((day) => (
                            <TableCell
                                key={day.dateKey}
                                align="center"
                                sx={{
                                    minWidth: showPsy && showNfb ? 130 : 110,
                                    fontWeight: "bold",
                                    backgroundColor: day.weekDay === SATURDAY_WEEKDAY ? SATURDAY_HIGHLIGHT_COLOR : "background.paper",
                                }}
                            >
                                <Typography variant="caption" display="block" fontWeight="bold">
                                    {day.weekDayLabel}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {day.dayOfMonth}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {UNIFIED_15MIN_SLOTS.map((time) => {
                        const isHourMark = time.endsWith(":00");

                        return (
                            <TableRow key={time}>
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1,
                                        backgroundColor: LIGHT_GREY,
                                        fontWeight: "medium",
                                        borderRight: "1px solid #e0e0e0",
                                        borderTop: isHourMark ? "2px solid #ffffff" : "none",
                                        py: 0.5,
                                        fontSize: "12px",
                                    }}
                                >
                                    {time}
                                </TableCell>
                                {days.map((day) => {
                                    const isSaturday = day.weekDay === SATURDAY_WEEKDAY;
                                    const psyActive = getPsyActiveSlot(day.dateKey, time);
                                    const nfbActive = getNfbActiveSlot(day.dateKey, time, day.weekDay);
                                    const availabilityStatus = getAvailabilityStatus(day.dateKey, time);

                                    const hasPsyReservation = psyActive.reservations.length > 0;
                                    const hasPsyBooking = psyActive.bookings.length > 0;
                                    const hasPsy = hasPsyReservation || hasPsyBooking;
                                    const hasNfb = nfbActive.reservations.length > 0 || nfbActive.bookings.length > 0;

                                    const psyBackgroundColor = hasPsyReservation
                                        ? PSY_RESERVATION_BLUE
                                        : hasPsyBooking
                                        ? PSY_BOOKING_ONLY_BG
                                        : getBackgroundColor(availabilityStatus);

                                    // An empty NFB slot in a blocked window must NOT look bookable (green).
                                    const nfbEmptyBackgroundColor = isNfbBlocked(day.dateKey, time)
                                        ? NFB_BLOCKED_GREY
                                        : getBackgroundColor(getAvailabilityStatus(day.dateKey, time, "nfb"));

                                    return (
                                        <TableCell
                                            key={`${day.dateKey}-${time}`}
                                            sx={{
                                                p: 0,
                                                height: 24,
                                                border: "none",
                                                borderTop: isHourMark ? "2px solid #ffffff" : "none",
                                                borderRight: isSaturday ? "3px solid #ffffff" : "1px dashed rgba(255,255,255,0.5)",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", height: "100%" }}>
                                                {showPsy && (
                                                    <Box
                                                        sx={{
                                                            width: showNfb ? "55%" : "100%",
                                                            backgroundColor: psyBackgroundColor,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        {hasPsy && psyActive.isStartRow && (
                                                            <ReservedSlotCell
                                                                reservation={psyActive.reservations[0]}
                                                                bookings={psyActive.bookings}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                {showNfb && (
                                                    <Box sx={{ width: showPsy ? "45%" : "100%", height: "100%" }}>
                                                        {hasNfb ? (
                                                            <NfbBlock
                                                                reservations={nfbActive.reservations}
                                                                bookings={nfbActive.bookings}
                                                                capacity={nfbActive.capacity}
                                                                isStartRow={nfbActive.isStartRow}
                                                                isEndRow={nfbActive.isEndRow}
                                                            />
                                                        ) : (
                                                            <Box sx={{ backgroundColor: nfbEmptyBackgroundColor, height: "100%" }} />
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
